/**
 * The signed-in Home's view (shared-client-screens D2, rows H1 to H12): the Mini App's Home body,
 * drawn the same in the app and on the phone website. Props only: the dashboard, the Chat chip's
 * unread count, whether Book is off, and where each part opens. It navigates nowhere itself; the
 * app's BookingHubPage passes the app's addresses (with the recipient token), the website's
 * PhoneSignedInHome the website's.
 *
 * Sections (mockup dashboard-mockup.html, F1 :1655 / F2 :2002): Waiting on you · Next-Session
 * hero · Book-a-Session card · quick actions · simplified tap-through Upcoming · Happening Soon ·
 * About. With no next session the hero and Upcoming are hidden and the Book card is promoted to
 * its hero variant. With nothing bookable there is no Book card at all (Gabriel 2026-11-03: a greyed
 * card "looks really bad").
 */
import type { BookingHubDashboard } from '@/types/dashboard';
import { PendingFormsAlert } from '@/components/client/PendingFormsAlert/PendingFormsAlert';

import { AboutCard, BookCard, HappeningSoon, NextSessionHero, QuickActions, UpcomingList } from './sections';
import styles from './BookingHubPage.module.css';

/** Where each part of Home opens; every one is required. */
export interface BookingHubHandlers {
  /** A sent form (H1). */
  openForm: (requestToken: string) => void;
  /** A waiting booking's Complete step or its pay page (web-account-pages D12). */
  openBookingStep: (bookingId: string, target: 'complete' | 'pay') => void;
  /** A booking's page (H3). */
  openBooking: (bookingId: string) => void;
  /** Book a Session (H2, Decision C). */
  book: () => void;
  /** The studio's chat (H4). */
  chat: () => void;
  waitlist: () => void;
  invite: () => void;
  /** Every upcoming booking (H6). */
  viewAllUpcoming: () => void;
  /** A Happening Soon event (H7). */
  openEvent: (eventId: string) => void;
  /** A Happening Soon series (H8). */
  openSeries: (seriesId: string) => void;
  /** Happening Soon's See all (H9). */
  seeAllEvents: () => void;
  /** The About page (H10). */
  about: () => void;
}

export interface BookingHubViewProps {
  dashboard: BookingHubDashboard;
  /** The Chat chip's badge (H5). */
  chatUnread: number;
  /** True when nothing is bookable: Home shows no Book card. */
  bookDisabled: boolean;
  on: BookingHubHandlers;
}

export function BookingHubView({ dashboard, chatUnread, bookDisabled, on }: BookingHubViewProps) {
  const next = dashboard.nextBooking;
  // Home is a glanceable dashboard: show only the soonest booking AFTER the hero
  // (one "…and after that" peek); the Bookings tab ("View all") owns the full list.
  const homeUpcoming = next ? dashboard.upcoming.filter((b) => b.id !== next.id).slice(0, 1) : [];

  return (
    <div className={styles.page} data-testid="booking-hub-page">
      {/* Waiting on you takes the top slot above the hero (both the hero and empty-Home
          variants); renders nothing when nothing waits. */}
      <PendingFormsAlert items={dashboard.waitingOnYou} onOpenForm={on.openForm} onOpenBooking={on.openBookingStep} />

      {next ? (
        <>
          <NextSessionHero booking={next} onOpen={() => on.openBooking(next.id)} />
          {!bookDisabled && <BookCard onBook={on.book} />}
        </>
      ) : (
        !bookDisabled && <BookCard variant="hero" onBook={on.book} />
      )}

      <QuickActions onChat={on.chat} chatUnread={chatUnread} onWaitlist={on.waitlist} onInvite={on.invite} />

      <UpcomingList bookings={homeUpcoming} onOpen={on.openBooking} onViewAll={on.viewAllUpcoming} />

      <HappeningSoon
        items={dashboard.happeningSoon}
        onOpenEvent={on.openEvent}
        onOpenSeries={on.openSeries}
        onSeeAll={on.seeAllEvents}
      />

      <AboutCard business={dashboard.business} onOpen={on.about} />
    </div>
  );
}
